import React, { useState, useEffect } from 'react';
import { Search, Plus, Calendar, Clock, CheckCircle } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const DemoArrearManagement = () => {
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [arrearHistory, setArrearHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [arrearForm, setArrearForm] = useState({
    arrear_type: '',
    amount: '',
    from_month: '',
    to_month: '',
    arrear_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const searchEmployees = async (query) => {
    if (!query.trim()) {
      setEmployeeSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?query=${encodeURIComponent(query)}&limit=100`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEmployeeSuggestions(data.employees || []);
      }
    } catch (error) {
      console.error('Error searching employees:', error);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchEmployees(employeeQuery);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [employeeQuery]);

  const loadHistory = async (employeeId) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/demo-arrears/employee/${employeeId}`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setArrearHistory(data || []);
      }
    } catch (error) {
      console.error('Error fetching arrear history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeQuery(`${employee.emp_code} - ${employee.full_name}`);
    setShowEmployeeSuggestions(false);
    loadHistory(employee.id);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setArrearForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveArrear = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;

    if (!arrearForm.arrear_type || !arrearForm.amount || !arrearForm.from_month || !arrearForm.to_month || !arrearForm.arrear_date) {
      showMessage('Please fill all required fields', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/demo-arrears'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: selectedEmployee.id,
          arrear_type: arrearForm.arrear_type,
          amount: parseFloat(arrearForm.amount),
          from_month: arrearForm.from_month,
          to_month: arrearForm.to_month,
          arrear_date: arrearForm.arrear_date,
          remarks: arrearForm.remarks
        })
      });

      if (response.ok) {
        showMessage('Arrear saved successfully', 'success');
        setShowAddModal(false);
        setArrearForm({
          arrear_type: '',
          amount: '',
          from_month: '',
          to_month: '',
          arrear_date: new Date().toISOString().slice(0, 10),
          remarks: ''
        });
        loadHistory(selectedEmployee.id);
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save arrear', 'error');
      }
    } catch (error) {
      showMessage('Error saving arrear', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Arrear Management</h1>
          <p className="text-sm text-gray-500">Standalone demo module for arrear entry</p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${
          messageType === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {message}
        </div>
      )}

      {/* Select Employee Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Employee</h3>
        <div className="relative max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={employeeQuery}
              onChange={(e) => {
                setEmployeeQuery(e.target.value);
                setShowEmployeeSuggestions(true);
              }}
              onFocus={() => setShowEmployeeSuggestions(true)}
              placeholder="Search by Employee Code or Name..."
              className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>

          {showEmployeeSuggestions && employeeSuggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-lg bg-white shadow-lg border border-gray-100 max-h-60 overflow-auto">
              <ul className="py-1">
                {employeeSuggestions.map((emp) => (
                  <li
                    key={emp.id}
                    onClick={() => handleEmployeeSelect(emp)}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-50"
                  >
                    <div className="font-medium text-gray-900">{emp.full_name}</div>
                    <div className="text-sm text-gray-500">{emp.emp_code} - {emp.department_name}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Selected Employee Info */}
      {selectedEmployee && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50/50 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Employee Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500">Employee Name</p>
                <p className="mt-1 font-medium text-gray-900">{selectedEmployee.full_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Employee Code</p>
                <p className="mt-1 font-medium text-gray-900">{selectedEmployee.emp_code}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Department</p>
                <p className="mt-1 font-medium text-gray-900">{selectedEmployee.department_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Designation</p>
                <p className="mt-1 font-medium text-gray-900">{selectedEmployee.designation_name || 'N/A'}</p>
              </div>
            </div>
            
            <div className="mt-6">
              <button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Arrear
              </button>
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Arrear History</h3>
            
            {loadingHistory ? (
              <div className="text-center py-8 text-gray-500">Loading history...</div>
            ) : arrearHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No arrear history found for this employee.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-4 py-3 font-medium">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Arrear Type</th>
                      <th className="px-4 py-3 font-medium">From Month</th>
                      <th className="px-4 py-3 font-medium">To Month</th>
                      <th className="px-4 py-3 font-medium">Arrear Date</th>
                      <th className="px-4 py-3 font-medium">Amount</th>
                      <th className="px-4 py-3 font-medium">Remarks</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {arrearHistory.map((arrear, idx) => (
                      <tr key={arrear.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{arrear.arrear_type}</td>
                        <td className="px-4 py-3">{arrear.from_month}</td>
                        <td className="px-4 py-3">{arrear.to_month}</td>
                        <td className="px-4 py-3">{arrear.arrear_date}</td>
                        <td className="px-4 py-3 font-medium">₹{arrear.amount.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={arrear.remarks}>{arrear.remarks || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${
                            arrear.status === 'Pending' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-green-50 text-green-700 border border-green-200'
                          }`}>
                            {arrear.status === 'Pending' ? <Clock className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                            {arrear.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Arrear Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Arrear</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <form onSubmit={handleSaveArrear} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Arrear Type *</label>
                  <select 
                    name="arrear_type" 
                    value={arrearForm.arrear_type} 
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Salary Difference">Salary Difference</option>
                    <option value="DA Difference">DA Difference</option>
                    <option value="Increment Arrear">Increment Arrear</option>
                    <option value="Promotion Arrear">Promotion Arrear</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Arrear Amount (₹) *</label>
                  <input 
                    type="number" 
                    name="amount" 
                    value={arrearForm.amount} 
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., 10000"
                    min="1"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">From Month *</label>
                    <input 
                      type="month" 
                      name="from_month" 
                      value={arrearForm.from_month} 
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">To Month *</label>
                    <input 
                      type="month" 
                      name="to_month" 
                      value={arrearForm.to_month} 
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Arrear Date *</label>
                  <input 
                    type="date" 
                    name="arrear_date" 
                    value={arrearForm.arrear_date} 
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
                  <textarea 
                    name="remarks" 
                    value={arrearForm.remarks} 
                    onChange={handleFormChange}
                    rows="3"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter any remarks..."
                  ></textarea>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowAddModal(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Arrear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoArrearManagement;
